import { parse } from 'odata-v4-filter-parser';
import _ from 'underscore';
import PerformanceMetricsColumns from '../common-columns/performance-metrics-columns';
import getLevel from './get-level';
import { getQueryPart } from './query-string';

const gridsMetadata = {
  Locations: {
    queryStringRegex: /LocationCriterion/,
    generateName: typeId => `settings_locations_${typeId}`,
    routeFragment: 'locations',
  },
  AdSchedule: {
    queryStringRegex: /DayTimeCriterion/,
    generateName: typeId => `settings_ad_schedule_${typeId}`,
    routeFragment: 'adschedule',
  },
  Devices: {
    queryStringRegex: {
      test: queryString => queryString.indexOf('DeviceCriterion') > -1 && queryString.indexOf('OSTargets') === -1,
    },
    generateName: typeId => `settings_devices_${typeId}`,
    routeFragment: 'devices',
  },
  DevicesOS: {
    queryStringRegex: /DeviceCriterion.*OSTargets/,
    generateName: typeId => `settings_devices_OS_${typeId}`,
    routeFragment: 'devices',
  },
  Age: {
    queryStringRegex: /AgeCriterion/,
    generateName: typeId => `summary_tab_demographics_Age_${typeId}`,
    routeFragmentForNewUI: 'demographics/age',
  },
  Gender: {
    queryStringRegex: /GenderCriterion/,
    generateName: typeId => `summary_tab_demographics_Gender_${typeId}`,
    routeFragmentForNewUI: 'demographics/gender',
  },
  CompanyName: {
    queryStringRegex: /CompanyNameCriterion/,
    generateName: typeId => `summary_tab_demographics_CompanyName_${typeId}`,
    routeFragmentForNewUI: 'demographics/company',
  },
  Industry: {
    queryStringRegex: /IndustryCriterion/,
    generateName: typeId => `summary_tab_demographics_Industry_${typeId}`,
    routeFragmentForNewUI: 'demographics/industry',
  },
  JobFunction: {
    queryStringRegex: /JobFunctionCriterion/,
    generateName: typeId => `summary_tab_demographics_JobFunction_${typeId}`,
    routeFragmentForNewUI: 'demographics/jobfunction',
  },
};

function findMetadata(queryString) {
  return _.find(gridsMetadata, ({ queryStringRegex }) => queryStringRegex.test(queryString));
}

function findMetadataKey(queryString) {
  return _.findKey(gridsMetadata, ({ queryStringRegex }) => queryStringRegex.test(queryString));
}

function isDemographics(queryString) {
  const key = findMetadataKey(queryString);
  return _.contains(['Age', 'Gender', 'CompanyName', 'Industry', 'JobFunction'], key);
}

export function getGridName({
  QueryString,
}, typeId) {
  const {
    generateName,
  } = findMetadata(QueryString);

  return generateName(typeId);
}

export function getGridColumnsMetadata({
  QueryString = '',
} = {}) {
  const mapping = _.defaults({
    AdGroupId: 'AdGroup/Id',
    AdGroupName: 'AdGroup/Name',
    BidAdj: 'CriterionBid',
    CashbackPercent: 'CriterionCashback', // this is override in campaigns/ad groups/audiences grid
    CampaignId: 'Campaign/Id',
    CampaignName: 'Campaign/Name',
    CampaignType: 'Campaign/CampaignType',
    Level: 'LevelOfDetails',
    DayTime: 'Criterion/Model.DayTimeCriterion/Day',
    DeviceName: 'Criterion/Model.DeviceCriterion/DeviceName',
    Age: 'Criterion/Model.AgeCriterion/AgeRange',
    Gender: 'Criterion/Model.GenderCriterion/GenderType',
    Company: 'Criterion/Model.CompanyNameCriterion/CompanyName',
    Industry: 'Criterion/Model.IndustryCriterion/Industry',
    JobFunction: 'Criterion/Model.JobFunctionCriterion/JobFunction',
    Delivery: 'DeliveryStatus',
    OSName: 'Criterion/Model.DeviceCriterion/OSName',
    Location: 'Criterion/Name',
  }, PerformanceMetricsColumns);

  if (isDemographics(QueryString)) {
    // For demographics, UI column name equals to MT property name
    //    demographics-grid\src\columns\bid-adj.js
    // For settings, UI column name does not equal MT property name
    //    targeting-settings-scaffolds\src\columns\bid-adjustment-column.es
    delete mapping.BidAdj;
  }

  return mapping;
}

function removeCriterion$typePredicate(originalFilter) {
  const filter = _.clone(originalFilter);
  if (filter.Criterion && filter.Criterion.$type) {
    delete filter.Criterion;
  }

  if (filter.$and) {
    filter.$and = _.reject(
      filter.$and,
      predicate => predicate.Criterion && predicate.Criterion.$type
    );

    if (filter.$and.length === 1) {
      return filter.$and[0];
    }
  }

  return _.isEmpty(filter) ? null : filter;
}

export function getGridFilter({
  QueryString,
}) {
  const filterPart = getQueryPart(QueryString, '$filter=');

  // settings tab uses $filter like "isof(Criterion, 'Model.DayTimeCriterion')"
  // but OData specification requires it to be "isof(Criterion, Model.DayTimeCriterion)"
  const fixed = decodeURIComponent(filterPart)
    .replace(/isof\((\w+),\s+'([.\w]+)'\)/g, (match, prop, type) => `isof(${prop},${type})`);

  // remove the default filter
  return removeCriterion$typePredicate(parse(fixed));
}

export function getGridRoute({
  CampaignId,
  AdGroupId,
  QueryString,
}, typeRoute) {
  const level = getLevel({
    CampaignId,
    AdGroupId,
  });

  const {
    routeFragment,
  } = findMetadata(QueryString);

  const routePart = `${routeFragment}/${typeRoute}`;

  switch (level) {
    case 'AdGroup':
      return `customer/:customer_id/account/:account_id/campaign/:campaign_id/adgroup/:adgroup_id/edit/${routePart}`;
    case 'Campaign':
      return `customer/:customer_id/account/:account_id/campaign/:campaign_id/edit/${routePart}`;
    case 'Account':
    default:
      return `customer/:customer_id/account/:account_id/campaign/settings/${routePart}`;
  }
}

export function getGridRouteForNewUI({
  getUrlSearch,
  entity,
  selection: {
    QueryString,
    CampaignId,
    AdGroupId,
  },
}) {
  const match = _.find(gridsMetadata, ({ queryStringRegex }) => queryStringRegex.test(QueryString));
  const path = match.routeFragmentForNewUI || match.routeFragment;
  const level = _.result({
    CampaignCriterion: 'campaign',
    AdGroupCriterion: 'adgroup',
  }, entity);
  const search = getUrlSearch({
    campaignId: CampaignId,
    adGroupId: AdGroupId,
  });

  return `${path}/${level}${search}`;
}
